import { useCallback, useEffect, useRef, useState } from 'react';

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** No script by now: say it's slow. A bad network was seen taking about 20 s. */
const SLOW_MS = 8_000;
/**
 * No script, or no answer from the widget, by now: stop waiting in silence and
 * offer a retry. Waiting on a person solving a challenge doesn't count, and an
 * answer that comes late still counts.
 */
const GIVE_UP_MS = 45_000;

type TurnstileApi = {
  ready?: (cb: () => void) => void;
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** True when a site key is configured — callers use it to gate submit. */
export const turnstileActive = Boolean(SITE_KEY);

let scriptPromise: Promise<void> | null = null;
let scriptTag: HTMLScriptElement | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  const pending = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    // A blocker can answer with an empty script, which still "loads".
    s.onload = () => (window.turnstile ? resolve() : reject(new Error('Turnstile is missing')));
    s.onerror = () => reject(new Error('failed to load Turnstile'));
    scriptTag = s;
    document.head.appendChild(s);
  });
  scriptPromise = pending;
  pending.catch(() => {
    if (scriptPromise === pending) forgetScript();
  });
  return pending;
}

/** The next load asks for the script again. */
function forgetScript() {
  scriptTag?.remove();
  scriptTag = null;
  scriptPromise = null;
}

/** What went wrong, in the visitor's terms: each ends on what to do about it. */
const FAILURES = {
  blocked: {
    title: "Verification didn't load, so you can't continue yet.",
    hint: 'An ad blocker or your network may be blocking it. Turn off the ad blocker or try another network, then try again.',
  },
  error: {
    title: "Verification failed, so you can't continue yet.",
    hint: 'This is usually the network or an ad blocker. Try another network or turn off the ad blocker, then try again.',
  },
  timeout: {
    title: "Verification timed out, so you can't continue yet.",
    hint: 'Your connection may be slow. Try again, and if it keeps happening, try another network or turn off an ad blocker.',
  },
  unsupported: {
    title: "This browser can't run the verification, so you can't continue yet.",
    hint: 'Update it or use another browser. If it is up to date, try another network or turn off an ad blocker.',
  },
} as const;

type Failure = keyof typeof FAILURES;
/** `loading`: no script yet. `slow`: the same, for a while. `widget`: it's drawn, no token yet. */
type Status = 'loading' | 'slow' | 'widget' | 'passed' | Failure;

/**
 * Cloudflare Turnstile widget. Renders nothing when VITE_TURNSTILE_SITE_KEY is
 * unset (dev / pre-provisioning), so signup/reset stay functional. `onVerify`
 * must be referentially stable (e.g. a useState setter); it fires with the
 * token on success and with "" on error/expiry.
 *
 * Callers hold their submit button until there's a token, so this says why
 * there isn't one: while the widget loads, and when it can't finish.
 */
export function Turnstile({ onVerify }: { onVerify: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;
    let widgetId: string | null = null;
    let giveUp: ReturnType<typeof setTimeout> | undefined;
    const fail = (why: Failure) => {
      if (cancelled) return;
      clearTimeout(giveUp);
      onVerify('');
      setStatus(why);
    };
    const waitForAnswer = () => {
      clearTimeout(giveUp);
      giveUp = setTimeout(() => fail('timeout'), GIVE_UP_MS);
    };

    setStatus('loading');
    waitForAnswer();
    const slow = setTimeout(
      () => setStatus((s) => (s === 'loading' ? 'slow' : s)),
      SLOW_MS,
    );
    loadScript()
      .then(() => {
        // Render directly once api.js has defined window.turnstile. Using
        // turnstile.ready() here silently no-ops (Cloudflare warns it "would
        // break if called before api.js is loaded"), leaving an empty widget.
        if (cancelled || !ref.current || !window.turnstile) return;
        clearTimeout(slow);
        waitForAnswer();
        setStatus('widget');
        try {
          widgetId = window.turnstile.render(ref.current, {
            sitekey: SITE_KEY,
            theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
            callback: (token: string) => {
              if (cancelled) return;
              clearTimeout(giveUp);
              onVerify(token);
              setStatus('passed');
            },
            // true: handled here, so Cloudflare's script doesn't throw as well.
            'error-callback': () => {
              fail('error');
              return true;
            },
            'expired-callback': () => {
              if (cancelled) return;
              onVerify('');
              setStatus('widget');
              waitForAnswer();
            },
            'timeout-callback': () => fail('timeout'),
            'unsupported-callback': () => fail('unsupported'),
            // A person is solving a challenge now; Cloudflare times that itself.
            'before-interactive-callback': () => clearTimeout(giveUp),
            'after-interactive-callback': waitForAnswer,
          });
        } catch {
          fail('error');
        }
      })
      .catch(() => {
        clearTimeout(slow);
        fail('blocked');
      });
    return () => {
      cancelled = true;
      clearTimeout(giveUp);
      clearTimeout(slow);
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          /* already gone */
        }
      }
    };
  }, [onVerify, attempt]);

  const retry = useCallback(() => {
    onVerify('');
    if (!window.turnstile) forgetScript();
    setAttempt((a) => a + 1);
  }, [onVerify]);

  if (!SITE_KEY) return null;
  const failure =
    status === 'loading' || status === 'slow' || status === 'widget' || status === 'passed'
      ? null
      : FAILURES[status];
  return (
    <div className="turnstile" data-testid="turnstile" data-status={status}>
      {/* Stays under the widget, which covers it once it has painted: the
          script being there doesn't mean the widget's frame has loaded. */}
      {(status === 'loading' || status === 'slow' || status === 'widget') && (
        <div className="turnstile-status">
          <p role="status" data-testid="turnstile-loading">
            <span className="turnstile-pulse" aria-hidden="true" />
            {status === 'slow'
              ? 'Still loading the verification. A slow network can take a moment.'
              : 'Loading the verification…'}
          </p>
        </div>
      )}
      <div ref={ref} className="turnstile-widget" />
      {failure && (
        <div className="turnstile-failed" role="alert" data-testid="turnstile-failed">
          <p className="turnstile-failed-title">{failure.title}</p>
          <p>{failure.hint}</p>
          <button type="button" className="btn btn-sm" onClick={retry} data-testid="turnstile-retry">
            Try again
          </button>
        </div>
      )}
    </div>
  );
}
